import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { useCallback } from 'react';
// @mui
import FormHelperText from '@mui/material/FormHelperText';
// utils
import { compressImage } from 'src/utils/imageCompression';
//
import { UploadAvatar, Upload, UploadBox } from '../upload';

// ----------------------------------------------------------------------

export function RHFUploadAvatar({ name, ...other }) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div>
          <UploadAvatar error={!!error} file={field.value} {...other} />

          {!!error && (
            <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
              {error.message}
            </FormHelperText>
          )}
        </div>
      )}
    />
  );
}

RHFUploadAvatar.propTypes = {
  name: PropTypes.string,
};

// ----------------------------------------------------------------------

export function RHFUploadBox({ name, ...other }) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <UploadBox files={field.value} error={!!error} {...other} />
      )}
    />
  );
}

RHFUploadBox.propTypes = {
  name: PropTypes.string,
};

// ----------------------------------------------------------------------

export function RHFUpload({ name, multiple, helperText, uploadSource, accept, ...other }) {
  const { control, setValue, getValues } = useFormContext();

  const handleDrop = useCallback(
    async (acceptedFiles) => {
      let locationData = null;

      // Ensure location is captured if required
      if (uploadSource === 'camera_location') {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
            });
          });
          locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          };
        } catch (err) {
          console.error('Failed to get location:', err);
          alert('Location is required for this image capture. Please ensure location services are enabled.');
          return; // Abort file addition if location is strictly required but fails
        }
      }

      // Compress images before adding to form
      const processedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          // Use location from file if already attached (e.g., by WebcamDialog)
          const fileLocation = file.location || locationData;

          // Automatically compress if it's an image
          const compressedFile = file.type.startsWith('image/')
            ? await compressImage(file, {
              maxWidth: 1920,
              maxHeight: 1920,
              quality: 0.8,
              maxSizeMB: 0.5, // Skip compression for files < 500KB
            })
            : file;

          // Add preview URL and attach location metadata
          return Object.assign(compressedFile, {
            preview: file.preview || URL.createObjectURL(compressedFile),
            ...(fileLocation && { location: fileLocation }), // Attach if available
          });
        })
      );

      if (multiple) {
        // Append to existing files
        const currentFiles = getValues(name) || [];
        const maxFiles = other.maxFiles || 10;
        const remainingSlots = maxFiles - (currentFiles?.length || 0);

        if (remainingSlots <= 0) {
          alert(`You can only upload a maximum of ${maxFiles} files.`);
          return;
        }

        const filesToAdd = processedFiles.slice(0, remainingSlots);

        if (processedFiles.length > remainingSlots) {
          alert(`Only ${remainingSlots} more file(s) can be added. Others were skipped.`);
        }

        setValue(name, [...currentFiles, ...filesToAdd], { shouldValidate: true });
      } else {
        setValue(name, processedFiles[0], { shouldValidate: true });
      }
    },
    [setValue, name, multiple, getValues, uploadSource, other.maxFiles]
  );

  const handleRemove = useCallback(
    (item) => {
      const currentFiles = getValues(name) || [];
      const filteredItems = currentFiles.filter((_file) => _file !== item);
      setValue(name, filteredItems, { shouldValidate: true });
    },
    [setValue, name, getValues]
  );

  const handleRemoveAll = useCallback(() => {
    setValue(name, [], { shouldValidate: true });
  }, [setValue, name]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Upload
          multiple={multiple}
          accept={accept}
          file={multiple ? undefined : field.value}
          files={multiple ? field.value : undefined}
          error={!!error}
          onDrop={handleDrop}
          onRemove={handleRemove}
          onRemoveAll={handleRemoveAll}
          helperText={
            (!!error || helperText) && (
              <FormHelperText error={!!error} sx={{ px: 2 }}>
                {error ? error?.message : helperText}
              </FormHelperText>
            )
          }
          uploadSource={uploadSource}
          maxFiles={other.maxFiles}
          {...other}
        />
      )}
    />
  );
}

RHFUpload.propTypes = {
  accept: PropTypes.object,
  helperText: PropTypes.string,
  multiple: PropTypes.bool,
  name: PropTypes.string,
  uploadSource: PropTypes.string,
};
